import type { PropsWithChildren } from 'react'
import toast from 'react-hot-toast'

import { AdminLink } from '@/components/shared/AdminLink'
import { carrierUri } from '@/constants'
import { useViewAsShipper } from '@/features/accounts/users/mutations'
import { NO_EXPORT_ATTR } from '@/features/reports/helpers'
import { useViewAsUser } from '@/features/users/mutations'

type BaseProps = {
  className?: string
  redirect?: string
  username: string
}

type CarrierProps = BaseProps & {
  type: 'carrier'
  userId: string
}

type ShipperProps = BaseProps & {
  type: 'shipper'
  userId: string
  accountId: string
}

export type ViewAsUserLinkProps = CarrierProps | ShipperProps

export const ViewAsUserLink = (
  props: PropsWithChildren<ViewAsUserLinkProps>,
) => {
  const viewAsCarrier = useViewAsUser('carrier-web')
  const viewAsShipper = useViewAsShipper()

  const handleCarrierClick = async () => {
    if (props.type !== 'carrier') return

    const { success } = await viewAsCarrier.mutateAsync({
      userId: props.userId,
    })
    if (!success) {
      toast.error('Error viewing as user')
      return
    }

    let uri = `${carrierUri}`
    if (props.redirect) {
      uri += `?redirect=${encodeURIComponent(props.redirect)}`
    }

    window.open(uri, 'view-as-carrier-web')
  }

  const handleShipperClick = () => {
    if (props.type !== 'shipper') return

    viewAsShipper.mutate({
      accountId: props.accountId,
      userId: props.userId,
      redirect: props.redirect,
    })
  }

  const handleClick = () => {
    if (props.type === 'carrier') {
      handleCarrierClick()
    } else {
      handleShipperClick()
    }
  }

  let content = props.children
  if (!content) {
    content = <>View as {props.username}</>
  }

  return (
    <AdminLink
      className={props.className}
      onClick={handleClick}
      {...{ [NO_EXPORT_ATTR]: true }}
    >
      {content}
    </AdminLink>
  )
}
